import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  ViewChild,
} from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  NgForm,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { Dialog } from 'primeng/dialog';
import { DividerModule } from 'primeng/divider';
import { FileUpload, FileUploadModule } from 'primeng/fileupload';
import { FloatLabelModule } from 'primeng/floatlabel';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { TextareaModule } from 'primeng/textarea';
import { FileSizePipe } from '../../../../pipes/file-size.pipe';
import { CompanyService } from '../../../../services/remote/company.service';
import { NotificationService } from '../../../../services/shared/notification.service';
import { Company } from '../../../../types/company';
import { QueryResult } from '../license/types/licenseTypes';

@Component({
  selector: 'cognos-company-form-dialog',
  imports: [
    ButtonModule,
    TableModule,
    TagModule,
    FormsModule,
    ReactiveFormsModule,
    FloatLabelModule,
    InputTextModule,
    TextareaModule,
    DividerModule,
    InputNumberModule,
    SelectModule,
    Dialog,
    FileUploadModule,
    FileSizePipe,
  ],
  templateUrl: './company-form-dialog.component.html',
  styleUrl: './company-form-dialog.component.scss',
})
export class CompanyFormDialogComponent implements OnChanges {
  @Input() company: Company | undefined = undefined;
  @Output() companyChange = new EventEmitter<any>();

  @Input() isOpen: boolean = false;
  @Output() isOpenChange = new EventEmitter<boolean>();

  public isEditing: boolean = false;
  public formGroup!: FormGroup;

  public logoFile: File | null = null;
  public logoSrc: string | null = null;

  @ViewChild('fileUploader') fileUploader!: FileUpload;

  constructor(
    private _companyService: CompanyService,
    private _notificationService: NotificationService,
    private fb: FormBuilder
  ) {
    this.formGroup = this.fb.group({
      name: ['', Validators.required],
      logo: [null],
    });

    this.isEditing = false;
  }

  ngOnChanges(changes: any) {
    if (changes.company) {
      if (changes.company.currentValue) {
        const companyToEdit = changes.company.currentValue;
        this.updateFormWithCompany(companyToEdit);
        // this.licenseId = licenseToEdit.id;
        this.isEditing = true;
      } else {
        // this.licenseId = undefined;
        this.isEditing = false;
      }
    }

    if (changes.isOpen) {
      this.logoSrc = this.company?.logo ?? null;
    }
  }

  async updateFormWithCompany(company: Company) {
    this.formGroup.setValue({
      name: company.name,
      logo: company.logo ?? '',
    });
  }

  onSelectFile(event: any) {
    const file = event.files[0];
    if (file) {
      // Store the file immediately to avoid any timing issues
      this.logoFile = file;
      
      // Create preview using FileReader
      const reader = new FileReader();
      reader.onload = () => {
        this.logoSrc = reader.result as string;
      };
      reader.readAsDataURL(file);
    } else {
      this.logoFile = null;
      this.logoSrc = null;
    }
  }

  onDeleteLogoClick() {
    this.logoSrc = null;
    this.formGroup.patchValue({
      logo: '',
    });
    this.resetFileUpload();
  }

  resetFileUpload() {
    if (this.fileUploader.files && this.fileUploader.files.length > 0) {
      this.fileUploader.clear();
      this.logoFile = null;
    }
  }

  public async onSubmitLicense(companyForm: any) {
    if (this.isEditing) {
      this._updatePreviousCompany(companyForm);
    } else {
      this._createNewCompany(companyForm);
    }
  }

  private async _createNewCompany(form: NgForm) {
    if (form.valid) {
      const newLogo = this.logoFile ?? null;
      const result = await this._companyService.createCompany(
        form.value,
        newLogo
      );
      this._manageSaveResponse(result);
    }
  }

  private async _updatePreviousCompany(form: NgForm) {
    if (!this.company?.id) return;

    const result = await this._companyService.editCompany(
      this.formGroup,
      this.company.id,
      this.logoFile
    );

    this.logoSrc = null;

    this._manageSaveResponse(result);
  }

  private _manageSaveResponse(result: any) {
    if (result === QueryResult.ok) {
      this._companyService.fetchCompanies();
      this.closeModal();
      this._notificationService.showSuccessNotification(
        'Data was successfully saved.'
      );
    } else {
      this._notificationService.showErrorNotification(
        'Error saving changes. Please try again.'
      );
      console.error('Error', result);
    }
  }

  public closeModal() {
    this.isOpenChange.emit(false);
    this.formGroup.reset();
    this.logoSrc = null;
    this.resetFileUpload();
    this.companyChange.emit(undefined);
  }
}
